import React, { useState } from "react";
import { Users, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";

const Row = ({ c, onPick, onDelete }) => (
  <div data-testid="saved-customer-row" className="group flex items-center gap-2 rounded-md px-2 py-2 hover:bg-[#F4F0E8]">
    <button data-testid="saved-customer-pick" onClick={() => onPick(c)} className="flex-1 min-w-0 text-left">
      <div className="text-sm font-medium text-[#1C1915] truncate">{c.name}</div>
      <div className="text-xs text-[#5C564E] truncate">{[c.email, c.phone].filter(Boolean).join(" · ") || c.address || "No contact details"}</div>
    </button>
    <button data-testid="saved-customer-delete" onClick={() => onDelete(c)} title="Delete saved customer" className="p-1.5 rounded-md text-[#A0998C] hover:text-[#9E2A2B] hover:bg-[#9E2A2B]/10 transition-colors">
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

export const CustomerPicker = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await http.get("/customers");
      setList(data.customers || []);
    } catch {
      setList([]);
      toast.error("Could not load saved customers.");
    }
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) {
      setQ("");
      load();
    }
  };

  const pick = (c) => {
    onPick(c);
    setOpen(false);
    toast.success(`${c.name}'s details filled in.`);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name} from saved customers? Their saved quotes are not affected.`)) return;
    try {
      await http.delete(`/customers/${c.id}`);
      setList((l) => l.filter((x) => x.id !== c.id));
      toast.success("Customer deleted.");
    } catch {
      toast.error("Could not delete that customer.");
    }
  };

  const shown = (list || []).filter((c) => `${c.name} ${c.email} ${c.phone} ${c.address} ${(c.jobs || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="saved-customers-button" className="inline-flex items-center gap-1.5 rounded-full border border-[#A6844A] px-3.5 py-1.5 text-sm text-[#1B3A4B] hover:bg-[#A6844A]/10 transition-colors">
          <Users className="w-4 h-4" /> Pick a saved customer
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2" data-testid="saved-customers-list">
        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A0998C]" />
          <Input data-testid="saved-customers-search" className="pl-8 h-9" placeholder="Search by name, job or location" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="max-h-72 overflow-y-auto">
          {list === null ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-[#A6844A]" /></div>
          ) : shown.length === 0 ? (
            <p data-testid="saved-customers-empty" className="py-5 px-2 text-sm text-center text-[#5C564E]">
              {list.length ? "No match." : "No saved customers yet — they're added automatically when you save a quote."}
            </p>
          ) : (
            shown.map((c) => <Row key={c.id} c={c} onPick={pick} onDelete={remove} />)
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
